import { memo, useMemo, useState } from 'react'

import { copy, LunyaMascot } from '~/modules/lunya'
import { isHttpError } from '~/shared/api'
import { ANALYTICS_EVENTS, useAnalytics } from '~/shared/lib/analytics'
import { notifications } from '~/shared/lib/notifications'
import { useHaptic } from '~/shared/platform'
import { Button, DatePickerSheet, Screen, Spacer, Text, TimePickerSheet, VStack } from '~/shared/ui'

import { UpdateBirthMutation } from '../../api'
import { BirthCityField, BirthDateField, BirthTimeField } from '../../components'
import { resolveBirthErrorText, resolveMaxBirthDate, validateBirthDate } from '../../utils'
import type { BirthDateErrorCode } from '../../utils'
import { CityPickerSheet } from '../CityPickerSheet'
import { AgeGateScreen } from './components/AgeGateScreen'
import {
  BIRTH_DATE_PICKER_DEFAULT,
  MASCOT_SIZE,
  TITLE_COLUMN_HEIGHT,
  TITLE_COLUMN_WIDTH,
} from './config'
import type { BirthStepWidgetProps } from './types'

import styles from './BirthStepWidget.module.scss'

type SheetKind = 'DATE' | 'TIME' | 'CITY' | null

/** A2 · Твоё небо. Дата рождения — единственное обязательное поле онбординга (спека 02 §A2). */
export const BirthStepWidget = memo(function BirthStepWidget(props: BirthStepWidgetProps) {
  const { displayName, gender, initialDate, initialTime, initialCityId, initialCityLabel, onNext } =
    props
  const { track } = useAnalytics()
  const haptic = useHaptic()

  const [date, setDate] = useState(initialDate)
  const [time, setTime] = useState(initialTime)
  const [cityId, setCityId] = useState(initialCityId)
  const [cityLabel, setCityLabel] = useState(initialCityLabel)
  const [openSheet, setOpenSheet] = useState<SheetKind>(null)
  /**
   * Отказ сервера по той же дате. Барабаны обрезаны по `maxBirthDate`, но дата приходит не
   * только пальцем: черновик онбординга переживает перезагрузку и лежит в адресе, а часы
   * человека могут врать. Последнее слово о возрасте — за сервером, и оно должно быть видно.
   */
  const [serverRejection, setServerRejection] = useState<BirthDateErrorCode | null>(null)

  const validation = useMemo(() => (date ? validateBirthDate(date) : null), [date])
  const rejection = validation?.valid === false ? validation.code : serverRejection
  const isTooYoung = rejection === 'TOO_YOUNG'
  const isFuture = rejection === 'FUTURE'
  const canSubmit = date !== null && validation?.valid === true

  // Верхняя граница барабана года: сегодня минус 14 лет (порог — в @lunya/contracts).
  const maxBirthDate = useMemo(() => resolveMaxBirthDate(), [])

  const [updateBirth, { isPending }] = UpdateBirthMutation.useMutation({
    // У онбординга свой разговор об этих двух отказах: возраст — полноэкранной заглушкой,
    // дата из будущего — ошибкой поля. Тост мутации тут был бы вторым голосом о том же.
    disabledSideEffects: ['onAnyError'],
    onAnyError: (error) => {
      const code = isHttpError(error) ? error.code : undefined

      if (code === 'AGE_RESTRICTED') {
        setServerRejection('TOO_YOUNG')
        return
      }
      if (code === 'BIRTH_DATE_IN_FUTURE') {
        setServerRejection('FUTURE')
        return
      }

      // Ненайденный город, сеть, 5xx — всё остальное говорит уведомлением, но говорит.
      notifications.error(resolveBirthErrorText(error))
    },
  })

  const handleDateChange = (nextDate: Date) => {
    setDate(nextDate)
    setServerRejection(null)
    haptic.impact('light')
    track(ANALYTICS_EVENTS.onbBirthdateSet())
  }

  const handleTimeChange = (nextTime: string) => {
    setTime(nextTime)
    track(ANALYTICS_EVENTS.onbBirthtimeSet())
  }

  const handleCitySelect = (city: { id: string; name: string }) => {
    setCityId(city.id)
    setCityLabel(city.name)
    track(ANALYTICS_EVENTS.onbCitySet())
  }

  const handleChangeDateFromAgeGate = () => {
    setDate(null)
    setServerRejection(null)
    setOpenSheet('DATE')
  }

  const handleSubmit = async () => {
    if (!canSubmit || !date) {
      return
    }

    if (!time) {
      track(ANALYTICS_EVENTS.onbSkipTime())
    }
    if (!cityId) {
      track(ANALYTICS_EVENTS.onbSkipCity())
    }

    // Дальше идём только если данные доехали: `mutate` не бросает и на отказе отдаёт undefined,
    // а раньше шаг закрывался в любом случае — отказ сервера по возрасту оставался незамеченным.
    const saved = await updateBirth({ date, time, cityId })
    if (!saved) return

    onNext({ date, time, cityId, cityLabel })
  }

  if (isTooYoung) {
    return <AgeGateScreen onChangeDate={handleChangeDateFromAgeGate} />
  }

  return (
    <Screen background='FLOW' hasStickyCta>
      <Screen.Content>
        <div className={styles.root}>
          <div className={styles.headerRow}>
            <div
              className={styles.titleColumn}
              style={{ width: TITLE_COLUMN_WIDTH, height: TITLE_COLUMN_HEIGHT }}
            >
              <Text variant='H1'>{copy.onb.a2.title({ name: displayName, gender })}</Text>
            </div>
            <div className={styles.mascot}>
              <LunyaMascot pose='ASKING' size={MASCOT_SIZE} />
            </div>
          </div>

          <Text variant='BODY_S' color='SECONDARY'>
            {copy.onb.a2.subtitle}
          </Text>

          <VStack gap='SMALL_L'>
            <BirthDateField
              value={date}
              errorText={isFuture ? copy.onb.a2.err_future : undefined}
              isActive={openSheet === 'DATE'}
              onPress={() => setOpenSheet('DATE')}
            />
            <BirthTimeField
              value={time}
              isActive={openSheet === 'TIME'}
              onPress={() => setOpenSheet('TIME')}
            />
            <BirthCityField
              value={cityLabel}
              isActive={openSheet === 'CITY'}
              onPress={() => setOpenSheet('CITY')}
            />
          </VStack>

          <Text variant='CAPTION' color='MUTED'>
            {copy.onb.a2.later}
          </Text>

          <Spacer size='MEDIUM_S' />
        </div>
      </Screen.Content>

      <Screen.Footer>
        <Button
          variant='PRIMARY'
          width='FULL'
          text={copy.onb.a2.cta}
          isDisabled={!canSubmit}
          isLoading={isPending}
          onPress={handleSubmit}
        />
      </Screen.Footer>

      <DatePickerSheet
        isOpen={openSheet === 'DATE'}
        onClose={() => setOpenSheet(null)}
        title={copy.onb.a2.date}
        value={date}
        defaultValue={BIRTH_DATE_PICKER_DEFAULT}
        maxDate={maxBirthDate}
        onChange={handleDateChange}
      />
      <TimePickerSheet
        isOpen={openSheet === 'TIME'}
        onClose={() => setOpenSheet(null)}
        title={copy.onb.a2.time}
        value={time}
        onChange={handleTimeChange}
      />
      <CityPickerSheet
        isOpen={openSheet === 'CITY'}
        onClose={() => setOpenSheet(null)}
        selectedCity={cityId && cityLabel ? { id: cityId, name: cityLabel } : null}
        onSelect={handleCitySelect}
      />
    </Screen>
  )
})
